import {
  Component,
  OnInit,
} from '@angular/core';
import { City, Model, Hub, ScheduledBooking } from '../../../classes/WSClasses';
import { Device } from '../../../classes/Device';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { WidgetsmanagerService } from '../../../services/widgetsmanager.service';
import { AlertController, NavParams, ModalController } from '@ionic/angular';
import { ScheduledBookingService } from 'src/app/services/scheduled-booking.service';
import { DeviceService } from 'src/app/services/device.service';
import { HttpStatusCodesEnum } from 'src/app/utils/httpStatusCodesEnum';

@Component({
  selector: 'app-scheduled-booking-detail',
  templateUrl: './scheduled-booking-detail.component.html',
  styleUrls: ['./scheduled-booking-detail.component.scss'],
})
export class ScheduledBookingDetailComponent implements OnInit {
  public scheduledBooking: ScheduledBooking;
  public isSpinnerVisible = false;
  public assignmentForm: FormGroup;
  public models: Model[];
  public cities: City[];
  public hubs: Hub[];
  public devices: Device[];
  public availableDevices: Device[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    public alertController: AlertController,
    private _scheduledBookingSvc: ScheduledBookingService,
    private _deviceSvc: DeviceService,
    private widgetsManager: WidgetsmanagerService,
    private modalController: ModalController,
    private navParams: NavParams
  ) {}

  ngOnInit(): void {
    this.scheduledBooking = this.navParams.get('scheduledBooking');
    this.models = this.navParams.get('models') || [];
    this.cities = this.navParams.get('cities') || [];
    this.hubs = this.navParams.get('hubs') || [];
    this.devices = this.navParams.get('devices') || [];

    // Filter available devices based on model and hub
    this.filterAvailableDevices();

    // Initialize assignment form
    this.assignmentForm = this.formBuilder.group({
      deviceId: [this.scheduledBooking && this.scheduledBooking.deviceId ? this.scheduledBooking.deviceId : null, [Validators.required]],
    });
  }

  private filterAvailableDevices(): void {
    // Filter devices by model and hub, and exclude already assigned devices
    this.availableDevices = this.devices.filter(device => 
      device.modelId === this.scheduledBooking.modelId
      //  && 
      // device.hubId === this.scheduledBooking.hubId &&
      // device.status === 'available' // Assuming devices have a status field
    );
  }

  public async closePopover(reloadParent = false): Promise<void> {
    await this.modalController.dismiss(reloadParent);
  }

  async assignDevice(): Promise<void> {
    if (!this.assignmentForm.valid) {
      return;
    }

    this.isSpinnerVisible = true;
    const deviceId = this.assignmentForm.get('deviceId').value;
    
    this._scheduledBookingSvc.assignDevice(this.scheduledBooking.id, deviceId).subscribe(
      (booking: ScheduledBooking) => {
        this.isSpinnerVisible = false;
        const successMsg = this.translate.instant('Device assigned successfully');
        this.widgetsManager.showToastMessage('success', successMsg);
        this.scheduledBooking = booking;
      },
      (err) => {
        this.isSpinnerVisible = false;
        let errorMsg = this.translate.instant('Error assigning device');
        if (err.status === HttpStatusCodesEnum.CONFLICT) {
          errorMsg = this.translate.instant('Device already assigned to another booking');
        }
        this.widgetsManager.showToastMessage('danger', errorMsg);
      }
    );
  }

  async unassignDevice(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('Unassign Device'),
      message: this.translate.instant('Are you sure you want to unassign this device?'),
      buttons: [
        {
          text: this.translate.instant('Cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translate.instant('Unassign'),
          handler: () => {
            this.isSpinnerVisible = true;
            this._scheduledBookingSvc.unassignDevice(this.scheduledBooking.id).subscribe(
              (booking: ScheduledBooking) => {
                this.isSpinnerVisible = false;
                const successMsg = this.translate.instant('Device unassigned successfully');
                this.widgetsManager.showToastMessage('success', successMsg);
                this.scheduledBooking = booking;
                this.assignmentForm.get('deviceId').setValue(null);
              },
              (err) => {
                this.isSpinnerVisible = false;
                const errorMsg = this.translate.instant('Error unassigning device');
                this.widgetsManager.showToastMessage('danger', errorMsg);
              }
            );
          },
        },
      ],
    });

    await alert.present();
  }

  async confirmBooking(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('Confirm Booking'),
      message: this.translate.instant('Are you sure you want to confirm this booking?'),
      buttons: [
        {
          text: this.translate.instant('Cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translate.instant('Confirm'),
          handler: () => {
            this.isSpinnerVisible = true;
            this._scheduledBookingSvc.confirm(this.scheduledBooking.id).subscribe(
              (booking: ScheduledBooking) => {
                this.isSpinnerVisible = false;
                const successMsg = this.translate.instant('Booking confirmed successfully');
                this.widgetsManager.showToastMessage('success', successMsg);
                this.scheduledBooking = booking;
              },
              (err) => {
                this.isSpinnerVisible = false;
                const errorMsg = this.translate.instant('Error confirming booking');
                this.widgetsManager.showToastMessage('danger', errorMsg);
              }
            );
          },
        },
      ],
    });

    await alert.present();
  }

  async cancelBooking(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('Cancel'),
      message: this.translate.instant('Are you sure you want to cancel this booking?'),
      buttons: [
        {
          text: this.translate.instant('Cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translate.instant('Confirm'),
          handler: () => {
            this.isSpinnerVisible = true;
            this._scheduledBookingSvc.refund(this.scheduledBooking.id).subscribe(
              () => {
                this.isSpinnerVisible = false;
                const successMsg = this.translate.instant('Booking cancelled successfully');
                this.widgetsManager.showToastMessage('success', successMsg);
                this.closePopover(true);
              },
              (err) => {
                this.isSpinnerVisible = false;
                const errorMsg = this.translate.instant('Error cancelling booking');
                this.widgetsManager.showToastMessage('danger', errorMsg);
              }
            );
          },
        },
      ],
    });

    await alert.present();
  }

  getStatusColor(status: string): string {
    const statusColors = {
      'pending': 'warning',
      'confirmed': 'success',
      'active': 'success',
      'completed': 'primary',
      'cancelled': 'danger',
      'expired': 'medium'
    };
    return statusColors[status] || 'medium';
  }

  getStatusText(status: string): string {
    const statusMap = {
      'pending': this.translate.instant('entity.scheduledBookingStatus.Pending'),
      'confirmed': this.translate.instant('entity.scheduledBookingStatus.Confirmed'),
      'active': this.translate.instant('entity.scheduledBookingStatus.Active'),
      'completed': this.translate.instant('entity.scheduledBookingStatus.Completed'),
      'cancelled': this.translate.instant('entity.scheduledBookingStatus.Cancelled'),
      'expired': this.translate.instant('entity.scheduledBookingStatus.Expired')
    };
    return statusMap[status] || status;
  }

  isBookingEditable(): boolean {
    return this.scheduledBooking.status === 'pending' || this.scheduledBooking.status === 'confirmed';
  }

  isBookingConfirmable(): boolean {
    return this.scheduledBooking.status === 'pending';
  }

  hasAssignedDevice(): boolean {
    return !!this.scheduledBooking.deviceId;
  }
}